import { useState, useMemo } from "react";
import { Guest, IContractModel, IReservation } from "../../interfaces";
import { AccommodationStep } from "./steps/accommodationStep";
import { ReservationStep } from "./steps/reservationStep";
import { GuestsStep } from "./steps/guestsStep";
import { DocumentStep } from "./steps/documentsStep";
import { StepReservationSteps, useGlobalWebCheckinUser } from "../../../../../hooks/WebCheckinUsers/global";
import { ReviewContractStep } from "./steps/reviewContractStep";
import { Content } from "../../style";
import { SignContractStep } from "./steps/signContractStep";

export interface IReservationStepsProps {
  onSave: (guests: Guest[], contract?: IContractModel) => void;
  reservation: IReservation;
  loading: boolean;
  containerGlobalRef: React.RefObject<HTMLDivElement>;
}

export const ReservationSteps: React.FC<IReservationStepsProps> = ({
  onSave,
  reservation,
  loading,
  containerGlobalRef,
}) => {
  const { contractModel, setActualReservationStep } = useGlobalWebCheckinUser();
  const [step, setStep] = useState<StepReservationSteps>(StepReservationSteps.AccommodationDetails);
  const [guests, setGuests] = useState<Guest[]>(reservation.guests);

  const primaryGuestName = useMemo(() => {
    return reservation.guests.find(g => g.primary)?.name || '';
  }, [reservation.guests]);

  const stepsMap = {
    [StepReservationSteps.AccommodationDetails]: (
      <Content>
        <AccommodationStep
          reservation={reservation}
          onNext={() => setStep(StepReservationSteps.ReservationDetails)}
        />
      </Content>
    ),

    [StepReservationSteps.ReservationDetails]: (
      <Content>
        <ReservationStep
          reservation={reservation}
          onNext={() => setStep(StepReservationSteps.Guests)}
          goBack={() => setStep(StepReservationSteps.AccommodationDetails)}
        />
      </Content>
    ),

    [StepReservationSteps.Guests]: (
      <Content>
        <GuestsStep
          guests={guests}
          setGuests={setGuests}
          reservation={reservation}
          onNext={() => setStep(StepReservationSteps.Documents)}
          goBack={() => setStep(StepReservationSteps.ReservationDetails)}
        />
      </Content>
    ),

    [StepReservationSteps.Documents]: (
      <Content
        ref={containerGlobalRef}
        style={{
          position: "relative",
          overflow: "hidden",
          display: loading ? "none" : "flex",
        }}>
        <DocumentStep
          reservation={reservation}
          onNext={() =>
            contractModel?.id ? setStep(StepReservationSteps.ReviewContract) : onSave(guests)
          }
          goBack={() => setStep(StepReservationSteps.Guests)}
          guests={guests}
          setGuests={setGuests}
        />
      </Content>
    ),

    [StepReservationSteps.ReviewContract]: (
      <Content>
        <ReviewContractStep
          reservation={reservation}
          onNext={() => {
            setActualReservationStep(StepReservationSteps.SignContract);
            setStep(StepReservationSteps.SignContract);
          }}
          goBack={() => setStep(StepReservationSteps.Documents)}
        />
      </Content>
    ),

    [StepReservationSteps.SignContract]: (
      <Content padding="20px 15px">
        <SignContractStep
          loading={loading}
          onNext={({ contract }) => onSave(guests, contract)}
          goBack={() => setStep(StepReservationSteps.ReviewContract)}
          guestName={primaryGuestName}
        />
      </Content>
    ),
  };

  return stepsMap[step] || null;
};
